import Phaser from "phaser";
import { Tower } from "../entities/Tower";
import { InputSystem, SwipeResult } from "../systems/InputSystem";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { TowerSystem } from "../systems/TowerSystem";
import { JudgeSystem, JudgeResult } from "../systems/JudgeSystem";
import { ScoreSystem } from "../systems/ScoreSystem";
import { StageSystem } from "../systems/StageSystem";
import { RetrySystem } from "../systems/RetrySystem";
import { UIHudSystem } from "../systems/UIHudSystem";
import { StageConfig } from "../data/StageConfig";
import { store } from "../state/StateStore";
import { createIdempotencyGuard } from "../utils/Idempotency";

export class PlayScene extends Phaser.Scene {
  private tower!: Tower;
  private inputSystem!: InputSystem;
  private physicsSystem!: PhysicsSystem;
  private towerSystem!: TowerSystem;
  private judgeSystem!: JudgeSystem;
  private scoreSystem!: ScoreSystem;
  private stageSystem!: StageSystem;
  private retrySystem!: RetrySystem;
  private uiHud!: UIHudSystem;

  private gameOverGuard = createIdempotencyGuard();
  private isTransitioning = false;
  private isCollapsing = false;

  constructor() {
    super({ key: "PlayScene" });
  }

  create(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const baseY = height - 150;

    // Background
    this.cameras.main.setBackgroundColor("#16213e");

    // Draw floor line
    const floorGraphics = this.add.graphics();
    floorGraphics.lineStyle(2, 0x555555, 0.5);
    floorGraphics.lineBetween(0, baseY + StageConfig.blockHeight / 2 + 5, width, baseY + StageConfig.blockHeight / 2 + 5);

    // Initialize tower
    this.tower = new Tower(this, centerX, baseY);

    // Initialize systems
    this.physicsSystem = new PhysicsSystem(this);
    this.towerSystem = new TowerSystem(this.tower);
    this.judgeSystem = new JudgeSystem(this.physicsSystem, this.towerSystem, this.tower);
    this.scoreSystem = new ScoreSystem(this.towerSystem);
    this.stageSystem = new StageSystem();
    this.retrySystem = new RetrySystem();
    this.uiHud = new UIHudSystem(this);
    this.inputSystem = new InputSystem(this, this.tower);

    // Reset guard
    this.gameOverGuard.release();
    this.isTransitioning = false;
    this.isCollapsing = false;

    // Build tower
    this.tower.build(StageConfig.blockCount);
    this.stageSystem.startStage();

    // Setup state
    store.set({ blocksRemaining: StageConfig.blockCount });

    // Create HUD
    this.uiHud.create();

    // Enable input
    this.inputSystem.enable(this.onSwipe.bind(this));

    // Instruction text
    const instrText = this.add
      .text(centerX, height - 60, "Swipe blocks left or right!", {
        fontSize: "16px",
        color: "#888888",
        fontFamily: "Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setDepth(200);

    // Fade out instruction after a moment
    this.tweens.add({
      targets: instrText,
      alpha: 0,
      delay: 2500,
      duration: 500,
      onComplete: () => instrText.destroy(),
    });
  }

  private onSwipe(result: SwipeResult): void {
    if (this.isTransitioning) return;
    if (result.block.isDarumaHead) return; // Can't swipe the daruma head

    // Apply physics impulse
    this.physicsSystem.applySwipeImpulse(result.block, result.deltaX);

    // Store swipe speed for scoring
    store.set({ lastSwipeSpeed: result.speed });
  }

  update(_time: number, _delta: number): void {
    if (this.isTransitioning) return;

    // Update tower visuals (continue during collapse)
    this.towerSystem.update();

    // During collapse, only update physics visuals — skip judging
    if (this.isCollapsing) return;

    // Judge current state
    const { result, escapedBlocks } = this.judgeSystem.judge();

    switch (result) {
      case JudgeResult.BlockEscaped:
        for (const block of escapedBlocks) {
          // Remove escaped block
          this.tower.removeBlock(block);
          this.stageSystem.blockRemoved();

          // Calculate and apply score
          const scoreGain = this.scoreSystem.applyScore(
            store.get().lastSwipeSpeed
          );

          // Show score popup
          this.uiHud.showScorePopup(scoreGain, block.getX(), block.getY());
          this.uiHud.showComboAnimation();
        }
        break;

      case JudgeResult.StageClear:
        // Remove escaped blocks first
        for (const block of escapedBlocks) {
          this.tower.removeBlock(block);
          this.stageSystem.blockRemoved();
          const scoreGain = this.scoreSystem.applyScore(
            store.get().lastSwipeSpeed
          );
          this.uiHud.showScorePopup(scoreGain, block.getX(), block.getY());
        }
        // Advance to next stage
        this.handleStageClear();
        break;

      case JudgeResult.TowerFell:
        this.handleGameOver();
        break;

      case JudgeResult.None:
        // Also check if daruma head fell off
        if (this.tower.darumaHead && !this.tower.darumaHead.isRemoved) {
          if (this.physicsSystem.hasFallenOff(this.tower.darumaHead, this.cameras.main.height)) {
            this.handleGameOver();
          }
        }
        break;
    }
  }

  private handleStageClear(): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    this.inputSystem.disable();

    const { width, height } = this.cameras.main;
    const centerX = width / 2;

    // Stage clear text
    const clearText = this.add
      .text(centerX, height / 2, "Stage Clear!", {
        fontSize: "36px",
        color: "#00ff88",
        fontFamily: "Arial, sans-serif",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(300)
      .setAlpha(0);

    this.tweens.add({
      targets: clearText,
      alpha: 1,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 500,
      yoyo: true,
      hold: 800,
      onComplete: () => {
        clearText.destroy();
        // Advance stage and rebuild
        this.stageSystem.advanceStage();
        this.tower.clear();
        this.tower.build(StageConfig.blockCount);
        store.set({ blocksRemaining: StageConfig.blockCount });
        this.stageSystem.startStage();
        this.isTransitioning = false;
        this.inputSystem.enable(this.onSwipe.bind(this));
      },
    });
  }

  private handleGameOver(): void {
    if (!this.gameOverGuard.tryAcquire()) return;
    this.isCollapsing = true;

    this.inputSystem.disable();
    this.retrySystem.gameOver();

    // Show "Game Over" text during collapse
    const { width, height } = this.cameras.main;
    const gameOverText = this.add
      .text(width / 2, height / 2, "Game Over", {
        fontSize: "36px",
        color: "#e74c3c",
        fontFamily: "Arial, sans-serif",
        fontStyle: "bold",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(300)
      .setAlpha(0);

    this.tweens.add({
      targets: gameOverText,
      alpha: 1,
      duration: 400,
    });

    // Let physics run so the collapse is visible, then transition
    this.time.delayedCall(1800, () => {
      this.isTransitioning = true;
      this.uiHud.destroy();
      this.tower.clear();
      this.scene.start("ResultScene");
    });
  }

  shutdown(): void {
    this.inputSystem?.destroy();
    this.uiHud?.destroy();
  }
}
